import { useImportModificationsMutation } from '@carboncopyco/data';
import {
  Button,
  InlineLink,
  Loading,
  TextBase,
  TextSubHeader,
} from '@carboncopyco/ui';
import { DemosSelectionTable } from './DemoSelectionTable';
import { useImportManagerState } from './ImportManagerContext';
import { ModificationsPanel } from './ModificationsPanel';

type ImportManagerProps = {
  onClose?: () => void;
  onImportEditsSuccess?: () => void;
  onImportEditsError?: () => void;
  onLearnMore?: () => void;
};

export const ImportManagerContent = ({
  onClose,
  onImportEditsSuccess,
  onImportEditsError,
  onLearnMore,
}: ImportManagerProps) => {
  const {
    isFetchingModifications,
    organizationId,
    selectedDemoId,
    draftId,
    indeterminateSchemas,
    selectedSchemas,
    totalSelectedModifications,
  } = useImportManagerState();
  const importButtonLabel = `Import ${
    totalSelectedModifications ? `(${totalSelectedModifications})` : ''
  }`;

  const [importModifications, { isLoading: isImporting }] =
    useImportModificationsMutation();

  return (
    <>
      <div className="flex flex-col gap-3 mb-4">
        <TextSubHeader label="Import Edits" />
        <div className="flex items-center gap-1">
          <TextBase
            color="text-ds-natural-600"
            label="Choose the demo from which you want to import edits. Imported edits will take precedence over existing ones."
          />
          <InlineLink
            label="Learn more"
            className="mb-0.5 cursor-pointer"
            href={
              !onLearnMore
                ? 'https://help.demostack.com/en/articles/8626304-import-edits'
                : undefined
            }
            target="_blank"
            rel="noopener noreferrer"
            onClick={onLearnMore}
          />
        </div>
        <div className="flex gap-3">
          <DemosSelectionTable />
          <ModificationsPanel />
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Button
          dark
          label="Cancel"
          onClick={onClose}
        />
        <Button
          label={isImporting ? '' : importButtonLabel}
          onClick={() =>
            importModifications({
              organizationId,
              schemaType: selectedSchemas.filter(
                (schema) => !indeterminateSchemas.includes(schema)
              ),
              entityId: selectedDemoId,
              draftId,
            })
              .unwrap()
              .then(onImportEditsSuccess)
              .catch(onImportEditsError)
          }
          disabled={
            isFetchingModifications ||
            !totalSelectedModifications ||
            isImporting
          }
          icon={
            isImporting ? <Loading color="text-ds-accent3-100" /> : undefined
          }
          style={{ width: 110 }}
        />
      </div>
    </>
  );
};
